import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Landmark, CarFront, Scale, Loader2, FileWarning, RefreshCcw, Gavel } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/* Civic — City Services in your pocket: licence status + renewal, your court
   cases and fines, hearing count, and lodging an appeal with the bench. */

const iso = (minAgo) => new Date(Date.now() - minAgo * 60000).toISOString();
const DEMO = {
  licence: { status: "valid", expires_at: iso(-43200), points: 2, class: "C" },
  fines_total: 750,
  upcoming_hearings: 1,
  cases: [
    { id: "c1", docket: "CRT-26-0142", charges: "Reckless driving", status: "verdict", verdict: { outcome: "guilty", fine: 750 }, created_at: iso(4320) },
    { id: "c2", docket: "CRT-26-0158", charges: "Noise complaint", status: "awaiting_hearing", created_at: iso(1200) },
  ],
};

export default function CivicApp({ demo, serverId, token, accent, sfx }) {
  const [data, setData] = useState(demo ? DEMO : null);
  const [appealFor, setAppealFor] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/civic/me`, { params: { token } })
      .then((r) => setData(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [demo, serverId, token]);
  useEffect(() => { load(); }, [load]);

  const renew = async () => {
    if (busy) return;
    setBusy("renew"); sfx?.keyTick();
    try {
      if (demo) { toast.success("Preview: licence renewed (demo)"); return; }
      const r = await api.post(`/nc/servers/${serverId}/dmv/renew`, {}, { params: { token } });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const appeal = async () => {
    if (busy || reason.trim().length < 10) return;
    setBusy("appeal");
    try {
      if (demo) { toast.success("Preview: appeal lodged (demo)"); setAppealFor(null); return; }
      const r = await api.post(`/nc/servers/${serverId}/civic/appeal`, { case_id: appealFor.id, reason }, { params: { token } });
      toast.success(r.data.message);
      setAppealFor(null); setReason("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!data) return <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-white/50" /></div>;

  const lic = data.licence || {};
  const licOk = lic.status === "valid";
  return (
    <div data-testid="civic-app" className="flex-1 flex flex-col gap-3 overflow-y-auto pb-4" style={{ scrollbarWidth: "none" }}>
      <div className="flex items-center gap-2">
        <p className="font-sans font-bold text-white text-lg flex-1">City Services</p>
        <button data-testid="civic-refresh" onClick={() => { sfx?.keyTick(); load(); }} className="text-white/40 hover:text-white"><RefreshCcw className="w-3.5 h-3.5" /></button>
      </div>

      {/* licence card */}
      <div className="border rounded-2xl p-4" style={{ borderColor: licOk ? "#34d39944" : "#f8717144", backgroundColor: licOk ? "#34d39910" : "#f8717110" }}>
        <div className="flex items-center gap-2.5">
          <CarFront className="w-5 h-5" style={{ color: licOk ? "#34d399" : "#f87171" }} />
          <div className="flex-1">
            <p className="font-mono text-[10px] font-bold text-white uppercase tracking-[0.15em]">Class {lic.class || "C"} licence</p>
            <p className="font-mono text-[8px] text-white/50 uppercase tracking-[0.15em] mt-0.5">
              {lic.status}{lic.points ? ` · ${lic.points} points` : ""}{lic.expires_at ? ` · expires ${new Date(lic.expires_at).toLocaleDateString()}` : ""}</p>
          </div>
          {!licOk && lic.status !== "none" && (
            <button data-testid="civic-renew" onClick={renew} disabled={busy === "renew"}
              className="font-mono text-[8px] uppercase tracking-[0.15em] px-3 py-2 rounded-xl font-bold text-black" style={{ backgroundColor: accent }}>
              Renew
            </button>
          )}
        </div>
      </div>

      {/* fines + hearings summary */}
      <div className="grid grid-cols-2 gap-2.5">
        <div className="border border-white/10 bg-white/[0.03] rounded-2xl p-3.5 text-center">
          <p className="font-sans font-bold text-xl text-amber-300">${(data.fines_total || 0).toLocaleString()}</p>
          <p className="font-mono text-[7px] uppercase tracking-[0.2em] text-white/40 mt-1">lifetime fines</p>
        </div>
        <div className="border border-white/10 bg-white/[0.03] rounded-2xl p-3.5 text-center">
          <p className="font-sans font-bold text-xl text-sky-300">{data.upcoming_hearings || 0}</p>
          <p className="font-mono text-[7px] uppercase tracking-[0.2em] text-white/40 mt-1">open hearings</p>
        </div>
      </div>

      {/* cases */}
      <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-white/40">Your record</p>
      {data.cases.length === 0 && <p className="font-mono text-[9px] text-white/30 text-center py-3">Clean record. Keep it that way.</p>}
      {data.cases.map((c) => (
        <div key={c.id} data-testid={`civic-case-${c.id}`} className="border border-white/10 bg-white/[0.03] rounded-2xl px-3.5 py-3">
          <div className="flex items-center gap-2">
            <Scale className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
            <p className="font-mono text-[10px] font-bold text-white flex-1 truncate">{c.docket}</p>
            <span className={`font-mono text-[7px] uppercase tracking-[0.15em] border px-1.5 py-0.5 rounded ${c.status === "verdict" ? "border-amber-400/40 text-amber-300" : "border-sky-400/40 text-sky-300"}`}>{c.status.replace("_", " ")}</span>
          </div>
          <p className="font-mono text-[9px] text-white/50 mt-1">{c.charges}</p>
          {c.verdict?.fine > 0 && <p className="font-mono text-[9px] text-amber-300 mt-1">Fined ${c.verdict.fine.toLocaleString()} · {c.verdict.outcome}</p>}
          {c.appeal && <p className="font-mono text-[8px] text-sky-300 mt-1 uppercase tracking-[0.15em]">appeal {c.appeal.status}</p>}
          {c.status === "verdict" && !c.appeal && (
            <button data-testid={`civic-appeal-${c.id}`} onClick={() => { setAppealFor(c); sfx?.keyTick(); }}
              className="mt-2 font-mono text-[8px] uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-lg border border-white/15 text-white/60 hover:text-white transition-colors flex items-center gap-1">
              <Gavel className="w-3 h-3" /> Lodge appeal
            </button>
          )}
        </div>
      ))}

      {/* appeal sheet */}
      {appealFor && (
        <div className="border border-white/15 bg-black/60 rounded-2xl p-4 flex flex-col gap-2.5">
          <p className="font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-white flex items-center gap-2"><FileWarning className="w-3.5 h-3.5 text-amber-300" /> Appeal — {appealFor.docket}</p>
          <textarea data-testid="civic-appeal-reason" rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Why should the bench overturn this verdict? (min 10 chars)"
            className="bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 font-mono text-[10px] text-white placeholder:text-white/30 focus:outline-none resize-none" />
          <div className="flex gap-2">
            <button data-testid="civic-appeal-submit" onClick={appeal} disabled={reason.trim().length < 10 || busy === "appeal"}
              className="flex-1 py-2.5 rounded-xl font-mono text-[9px] uppercase tracking-[0.2em] font-bold text-black disabled:opacity-40" style={{ backgroundColor: accent }}>
              Submit to the bench
            </button>
            <button onClick={() => setAppealFor(null)} className="px-4 py-2.5 rounded-xl border border-white/15 font-mono text-[9px] uppercase tracking-[0.2em] text-white/60">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
